import React, { useEffect } from 'react';
import { WithQueryWidgetProps } from './withQueryWidget';
import QueryWidgetContentContainer from './QueryWidgetContentContainer';
import { ActiveWidgetRequest, WidgetCache, WidgetResponseItem } from 'system/widget/Widget';
import APIRequest, { APIError, enqueueAPIErrorSnackbar } from 'system/APIRequest';
import WidgetLoader from '../WidgetLoader';
import { useDashboard } from 'routes/dashboard/Dashboard';
import { useTemplateQueries } from 'routes/dashboard/DashboardPageRoot';
import { enqueueSnackbar } from 'notistack';

async function fetchWidgetRequest(activeWidgetRequest: ActiveWidgetRequest): Promise<WidgetResponseItem> {
  let fullEndpoint = activeWidgetRequest.endpoint;
  if (activeWidgetRequest.activeQueryIds && activeWidgetRequest.activeQueryIds.length > 0) {
    // if the last character is not a slash, add one
    if (!fullEndpoint.endsWith('/')) {
      fullEndpoint += '/';
    }
    // append the active query IDs to the endpoint
    fullEndpoint += activeWidgetRequest.activeQueryIds.join(',');
  }

  const requestInit = { ...(activeWidgetRequest.requestInit || {}) } as RequestInit;

  // Ensure the requestInit has a stringified body
  if (requestInit && requestInit.body && typeof requestInit.body !== 'string') {
    requestInit.body = JSON.stringify(requestInit.body);
  }

  // Default to POST if no method is specified
  if (requestInit && !requestInit.method) {
    requestInit.method = 'POST';
  }

  const requestInit2 = {
    body: requestInit.body,
    method: requestInit.method || 'POST'
  };

  return APIRequest(fullEndpoint, activeWidgetRequest.queryParams || {}, requestInit2, false, 3, [400, 401]).then((response: any) => {
    if (activeWidgetRequest.originalRequest?.responseTransform) {
      response = activeWidgetRequest.originalRequest.responseTransform(response);
    }
    const widgetResponse = {
      queryDisplayTitle: activeWidgetRequest.queryDisplayTitle,
      requestName: activeWidgetRequest.name,
      originatingRequest: activeWidgetRequest,
      runAt: new Date().toISOString(),
      responseBody: response
    } as WidgetResponseItem;

    return widgetResponse;
  });
}

async function fetchWidgetRequestGroup(activeWidgetRequestGroup: ActiveWidgetRequest[] | ActiveWidgetRequest): Promise<WidgetResponseItem[] | WidgetResponseItem> {
  if (Array.isArray(activeWidgetRequestGroup)) {
    const responses = await Promise.all(activeWidgetRequestGroup.map(activeWidgetRequest => {
      return fetchWidgetRequest(activeWidgetRequest);
    }));
    return responses;
  } else {
    return fetchWidgetRequest(activeWidgetRequestGroup);
  }
}

async function fetchAllWidgetRequests(allActiveWidgetRequests: Array<ActiveWidgetRequest | ActiveWidgetRequest[]>): Promise<Array<WidgetResponseItem | WidgetResponseItem[]>> {
  return await Promise.all(
    allActiveWidgetRequests.map((requestInfo: ActiveWidgetRequest | ActiveWidgetRequest[]) => {
      return fetchWidgetRequestGroup(requestInfo);
    })
  );
}

// Handle the API calls needed by a QueryWidget to fetch its data
export default function QueryWidgetAPIWrapper(props: WithQueryWidgetProps): JSX.Element {
  const dashboard = useDashboard();
  const templateQueriesInfo = useTemplateQueries();

  // apiResponses
  const [apiResponses, setApiResponses] = React.useState<Array<WidgetResponseItem | WidgetResponseItem[]>>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  useEffect(() => {
    const activeWidgetRequests = props.activeWidgetRequests,
          widgetQueries = props.widgetQueries;
    if (!activeWidgetRequests || activeWidgetRequests.length === 0 || props.widget.isCached) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    fetchAllWidgetRequests(activeWidgetRequests).then((responses: Array<WidgetResponseItem | WidgetResponseItem[]>) => {
      setApiResponses(responses);
      setIsLoading(false);
      // If the dashboard is modifiable and not a template, we need to update the cache
      if (dashboard._modifiable && !dashboard.template_type) {
        const allDashboardQueries = templateQueriesInfo.rawTemplateQueries;
        const allQueries = [...(widgetQueries || []), ...(allDashboardQueries || [])];

        // Don't store duplicate queries in the cache to save space
        const seenQueryIds = new Set<string>();
        const uniqueQueries = allQueries.filter(query => {
          if (query.id && !seenQueryIds.has(query.id)) {
            seenQueryIds.add(query.id);
            return true;
          }
          return false;
        });

        const newCache = {
          cachedAt: new Date().toISOString(),
          cacheItems: responses,
          cachedQueries: uniqueQueries,
          cachedRequests: activeWidgetRequests
        } as WidgetCache;

        props.saveWidgetDataCache(newCache).then(() => {
          // enqueueSnackbar('Widget updated successfully', { variant: 'success', autoHideDuration: 2000 });
        }).catch((error: any) => {
          enqueueAPIErrorSnackbar(error, enqueueSnackbar);
        });
      }
    }).catch((error: APIError) => {
      enqueueAPIErrorSnackbar(error, enqueueSnackbar);
      setIsLoading(false);
    });
  }, [templateQueriesInfo.rawTemplateQueries, props.activeWidgetRequests]);

  return (
    <>
      {isLoading && (
        <WidgetLoader />
      )}
      {!isLoading && apiResponses && (
        <QueryWidgetContentContainer
          {...props}
          apiResponses={apiResponses}
        >
        </QueryWidgetContentContainer>
      )}
    </>
  );
}
